import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, RotateCcw, Paperclip, Calendar as CalendarIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { canApprove } from "../lib/permissions";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export default function MyOrders() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [revision, setRevision] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/orders/mine/review").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (order, decision) => {
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/feedback`, { decision, message });
      toast.success(decision === "approve" ? t("approved") : t("revision_sent"));
      setRevision(null); setMessage("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const inReview = orders.filter((o) => o.status === "review");
  const done = orders.filter((o) => o.status === "completed");

  const Card = ({ o, actions }) => (
    <div data-testid={`my-order-${o.id}`} className="glass rounded-2xl p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <button onClick={() => nav(`/orders?open=${o.id}`)} className="font-semibold text-left hover:underline" style={{ fontFamily: "Outfit" }}>{o.title}</button>
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="border-white/10 bg-white/5 text-[10px]">{t(o.type)}</Badge>
            {o.deadline && <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3" />{o.deadline.slice(0, 10)}</span>}
            <span>· {t("created_by")} {o.created_by_name}</span>
          </div>
        </div>
        <Badge variant="outline" className={o.status === "review" ? "border-violet-400/30 bg-violet-400/10 text-violet-200" : "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"}>{t(o.status)}</Badge>
      </div>
      {o.deliverables?.length > 0 && (
        <div className="text-xs text-slate-300 space-y-1">
          {o.deliverables.slice(-1).map((d) => (
            <div key={d.id} className="bg-white/5 rounded-lg p-2.5">
              <div className="text-slate-400 mb-1">{d.delivered_by_name} · {new Date(d.delivered_at).toLocaleString()}</div>
              {d.message}
              {d.files?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {d.files.map((f) => (
                    <a key={f.id} href={`${api.defaults.baseURL}/files/${f.storage_path}?auth=${localStorage.getItem("access_token") || ""}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/5 border border-white/10 hover:bg-white/10">
                      <Paperclip className="w-3 h-3" />{f.original_filename}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {actions && canApprove(user, o) && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" data-testid={`approve-btn-${o.id}`} disabled={busy} onClick={() => send(o, "approve")} className="bg-emerald-500 hover:bg-emerald-600">
            <CheckCircle2 className="w-4 h-4 mr-1.5" />{t("approve")}
          </Button>
          <Button size="sm" variant="outline" data-testid={`revision-btn-${o.id}`} onClick={() => { setRevision(o); setMessage(""); }} className="border-amber-400/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20">
            <RotateCcw className="w-4 h-4 mr-1.5" />{t("request_revision")}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="my-orders-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>{t("my_orders")}</h1>
        <p className="text-slate-400 mt-1 text-sm">{t("my_orders_hint")}</p>
      </div>

      <section>
        <div className="text-xs uppercase tracking-widest text-violet-300 mb-3">{t("to_review")} · {inReview.length}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {inReview.map((o) => <Card key={o.id} o={o} actions />)}
          {inReview.length === 0 && <div className="col-span-full text-sm text-slate-500 py-8 text-center glass rounded-2xl">{t("no_data")}</div>}
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-3">{t("completed")} · {done.length}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {done.map((o) => <Card key={o.id} o={o} />)}
          {done.length === 0 && <div className="col-span-full text-sm text-slate-500 py-8 text-center glass rounded-2xl">{t("no_data")}</div>}
        </div>
      </section>

      <Dialog open={!!revision} onOpenChange={(v) => !v && setRevision(null)}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader><DialogTitle>{t("request_revision")} — {revision?.title}</DialogTitle></DialogHeader>
          <Textarea data-testid="revision-message-input" placeholder={t("revision_placeholder")} value={message} onChange={(e) => setMessage(e.target.value)} className="bg-white/5 border-white/10" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRevision(null)}>{t("cancel")}</Button>
            <Button data-testid="revision-submit-btn" disabled={busy || !message.trim()} onClick={() => send(revision, "revision")} className="bg-amber-500 hover:bg-amber-600 text-black">{busy ? "…" : t("submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
